import type { LocationQuery, RouteLocation, RouteRecordRaw } from 'vue-router'
import { ROUTES } from '@dashboard/router/routeNames'
import { integrationTabFromLegacyQuery } from '@dashboard/modules/integration/integrationTabs'
import { requireHostedPages } from '@dashboard/modules/pages/pagesAccess'

const SettingsLayout = () => import('./pages/SettingsLayout.vue')

const keep = (to: RouteLocation, name: string, query?: LocationQuery) => ({
  name,
  params: { teamPid: to.params.teamPid },
  query: query ?? to.query,
  hash: to.hash
})

const projectPages: RouteRecordRaw[] = [
  {
    name: ROUTES.SETTINGS_GENERAL,
    path: 'general',
    component: () => import('./pages/project/GeneralPage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'General',
      description: "Your project's name and website, and what the dashboard leads with."
    }
  },
  {
    name: ROUTES.SETTINGS_WEBSITE,
    path: 'website',
    component: () => import('./pages/project/WebsitePage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Website & install',
      description: 'Install RightMessage on your site and choose the domains it runs on.'
    }
  },
  {
    name: ROUTES.SETTINGS_PAGE_DOMAINS,
    path: 'page-domains',
    component: () => import('./pages/project/PageDomainsPage.vue'),
    beforeEnter: requireHostedPages,
    meta: {
      settingsScope: 'project',
      title: 'Page domains',
      description: 'Where your hosted pages live: your RightMessage subdomain or a domain of your own.'
    }
  },
  {
    name: ROUTES.INTEGRATIONS,
    path: 'integrations',
    component: () => import('@dashboard/modules/integration/pages/IntegrationsIndex.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Integrations',
      description: 'Connect your email platform, analytics tools and apps.'
    }
  },
  {
    name: ROUTES.INTEGRATION,
    path: 'integrations/:integration',
    component: () => import('@dashboard/modules/integration/pages/IntegrationShow.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Integrations'
    }
  },
  {
    name: ROUTES.SETTINGS_FORMS,
    path: 'forms',
    component: () => import('./pages/project/FormsPage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Forms & widgets',
      description: 'What visitors see when they use your forms, and how those forms behave.'
    }
  },
  {
    name: ROUTES.SETTINGS_PRIVACY,
    path: 'privacy',
    component: () => import('./pages/project/PrivacyPage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Privacy & data',
      description: "Control what RightMessage collects, who's left out of reports, and your project's data."
    }
  },
  {
    name: ROUTES.SETTINGS_AI,
    path: 'ai',
    component: () => import('./pages/project/AiContextPage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'AI context',
      description: 'What Righty knows about your business when it writes and suggests.'
    }
  },
  {
    name: ROUTES.SETTINGS_COLLABORATORS,
    path: 'collaborators',
    component: () => import('./pages/project/CollaboratorsPage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Collaborators',
      description: 'Invite people to work on this project with you.',
      requiredPermission: 'manage-collaborators'
    }
  },
  {
    name: ROUTES.SETTINGS_BILLING,
    path: 'billing',
    component: () => import('./pages/project/PlanBillingPage.vue'),
    meta: {
      settingsScope: 'project',
      title: 'Plan & billing',
      description: 'Your plan for this project and how you pay for it.',
      requiredPermission: 'manage-billing',
      settingsAccess: 'perTeamBilling'
    }
  },
  // Legacy settings paths still used by emails and bookmarks.
  { path: 'options', redirect: (to) => keep(to, ROUTES.SETTINGS_GENERAL) },
  { path: 'domains', redirect: (to) => keep(to, ROUTES.SETTINGS_WEBSITE) },
  { path: 'snippet', redirect: (to) => keep(to, ROUTES.SETTINGS_WEBSITE) },
  {
    path: 'integration',
    redirect: (to) => keep(to, ROUTES.INTEGRATIONS, integrationTabFromLegacyQuery(to.query))
  },
  {
    path: 'integration/:integration',
    redirect: (to) => ({ ...keep(to, ROUTES.INTEGRATION), params: to.params })
  },
  {
    path: 'attribution',
    redirect: (to) => ({ ...keep(to, ROUTES.INTEGRATIONS, {}), hash: '#lead-attribution' })
  },
  { path: 'branding', redirect: (to) => keep(to, ROUTES.SETTINGS_FORMS) },
  { path: 'advanced', redirect: (to) => keep(to, ROUTES.ACCOUNT_PREFERENCES) },
  { path: 'data', redirect: (to) => keep(to, ROUTES.SETTINGS_PRIVACY) }
]

const accountPages: RouteRecordRaw[] = [
  {
    name: ROUTES.ACCOUNT_PROFILE,
    path: 'profile',
    component: () => import('./pages/account/ProfilePage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Profile',
      description: 'Your name and the email you sign in with.'
    }
  },
  {
    name: ROUTES.ACCOUNT_SECURITY,
    path: 'security',
    component: () => import('./pages/account/SecurityPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Sign-in & security',
      description: 'Your password, two-factor authentication, and where you’re signed in.'
    }
  },
  {
    name: ROUTES.ACCOUNT_NOTIFICATIONS,
    path: 'notifications',
    component: () => import('./pages/account/NotificationsPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Notifications',
      description: 'Emails we send you about your projects.'
    }
  },
  {
    name: ROUTES.ACCOUNT_PREFERENCES,
    path: 'preferences',
    component: () => import('./pages/account/PreferencesPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Preferences',
      description: 'How the dashboard looks and behaves for you.'
    }
  },
  {
    name: ROUTES.ACCOUNT_PROJECTS,
    path: 'projects',
    component: () => import('./pages/account/ProjectsPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Projects',
      description: 'Every project you own or work on.'
    }
  },
  {
    name: ROUTES.ACCOUNT_ORGANIZATIONS,
    path: 'organizations',
    component: () => import('./pages/account/OrganizationsPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Organizations',
      description: 'The organizations you belong to and who works in them.'
    }
  },
  {
    name: ROUTES.ACCOUNT_ORGANIZATION,
    path: 'organizations/:orgPid',
    component: () => import('./pages/account/OrganizationPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Organizations'
    }
  },
  {
    name: ROUTES.ACCOUNT_API,
    path: 'api',
    component: () => import('./pages/account/ApiAccessPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'API & AI access',
      description: 'Personal API tokens and the AI assistants that act as you.'
    }
  },
  {
    name: ROUTES.ACCOUNT_BILLING,
    path: 'billing',
    component: () => import('./pages/account/BillingPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Billing',
      description: 'Your account-wide plan, usage and invoices.',
      settingsAccess: 'legacyBilling'
    }
  },
  {
    name: ROUTES.ACCOUNT_AFFILIATE,
    path: 'affiliate',
    component: () => import('./pages/account/AffiliatePage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Affiliate',
      description: 'Get credit for the customers you send to RightMessage.'
    }
  },
  {
    name: ROUTES.AGENCY_PROFILE,
    path: 'agency',
    component: () => import('./pages/agency/AgencyProfilePage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Agency profile',
      description: 'How your agency appears on client signup pages and in the partner directory.',
      settingsAccess: 'agency'
    }
  },
  {
    name: ROUTES.AGENCY_CLIENTS,
    path: 'agency/clients',
    component: () => import('./pages/agency/AgencyClientsPage.vue'),
    meta: {
      settingsScope: 'account',
      title: 'Clients & invitations',
      description: 'Invite clients and see who signed up through your link.',
      settingsAccess: 'agency'
    }
  }
]

const LEGACY_PROFILE_PAGES: Record<string, string> = {
  personal: ROUTES.ACCOUNT_PROFILE,
  projects: ROUTES.ACCOUNT_PROJECTS,
  'ai-connections': ROUTES.ACCOUNT_API,
  'api-tokens': ROUTES.ACCOUNT_API,
  agency: ROUTES.AGENCY_PROFILE,
  'agency-clients': ROUTES.AGENCY_CLIENTS,
  // Project billing forwards account-wide legacy owners on to Your account › Billing.
  billing: ROUTES.SETTINGS_BILLING
}

export default [
  {
    name: ROUTES.SETTINGS,
    path: 'settings',
    component: SettingsLayout,
    redirect: (to) => keep(to, ROUTES.SETTINGS_GENERAL),
    meta: { settingsScope: 'project' },
    children: projectPages
  },
  {
    name: ROUTES.ACCOUNT,
    path: 'account',
    component: SettingsLayout,
    redirect: (to) => keep(to, ROUTES.ACCOUNT_PROFILE),
    meta: { settingsScope: 'account' },
    children: accountPages
  },
  {
    path: 'profile/:page?',
    redirect: (to) => {
      const page = typeof to.params.page === 'string' ? to.params.page : ''
      return keep(to, LEGACY_PROFILE_PAGES[page] ?? ROUTES.ACCOUNT_PROFILE)
    }
  },
  {
    name: ROUTES.UPGRADE,
    path: 'upgrade',
    component: () => import('./pages/Upgrade.vue')
  },
  {
    name: ROUTES.REVIEW_SESSION,
    path: 'review-session',
    component: () => import('./pages/ReviewSession.vue')
  }
] satisfies RouteRecordRaw[]
